/**
 * AI Narrative Engine
 * Generates plain-English daily briefings from location data.
 * "What happened, why, and what to do next."
 */
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { BookOpen, MapPin, TrendingUp, TrendingDown, AlertTriangle, Trophy, ChevronDown, Clock, Sparkles, ArrowRight } from 'lucide-react';
import { QSR_DEMO_LOCATIONS } from '@/config/qsrConfig';
import { getIndustryTerms, resolveIndustryKey } from '@/config/industryTerminology';

const generateNarratives = () => {
  const now = new Date();
  const dayNames = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];
  const yesterday = dayNames[(now.getDay() + 6) % 7];

  return {
    headline: `${yesterday} was a strong day across the network — revenue hit $54,200, up 7% vs the ${yesterday} average. Southport led with its best ${yesterday} in 6 weeks.`,
    locationStories: [
      {
        location: 'Southport',
        state: 'QLD',
        sentiment: 'positive',
        narrative: `Southport had its best ${yesterday} in 6 weeks (+14% vs avg). The driver was a 22% spike in combo upsells after Sarah's team adopted the new suggestive sell script. Drive-through throughput also hit 138 cars/hr during lunch — a new weekly high. SPLH reached $74.30, well above the $65 target.`,
        keyMetric: { label: 'SPLH', value: '$74.30', trend: '+12%' },
        driver: 'Suggestive sell script adoption',
      },
      {
        location: 'Penrith',
        state: 'NSW',
        sentiment: 'positive',
        narrative: `Penrith held steady with $12,800 in daily revenue (+3% vs avg). Drive-through accuracy remained excellent at 98.5%. The new pre-staging workflow reduced average DT wait from 3.4 to 3.1 minutes. Labour cost stayed within target at 24.2%.`,
        keyMetric: { label: 'DT Wait', value: '3.1 min', trend: '-0.3 min' },
        driver: 'Pre-staging workflow',
      },
      {
        location: 'Prospect',
        state: 'SA',
        sentiment: 'negative',
        narrative: `Prospect had a tough ${yesterday} — SPLH dropped to $51.20, the lowest in the network. Root cause: 2 staff called in sick and the replacement wasn't cross-trained for the grill station, causing a 15-minute kitchen bottleneck during lunch. Revenue fell 8% below the ${yesterday} average.`,
        keyMetric: { label: 'SPLH', value: '$51.20', trend: '-7.6%' },
        driver: 'Staff absence + cross-training gap',
      },
      {
        location: 'Footscray',
        state: 'VIC',
        sentiment: 'neutral',
        narrative: `Footscray was steady — $8,200 revenue, in line with averages. DT wait time crept to 4.9 min during the 12:30pm rush (target: 3.5 min). The bottleneck was the single payment window. Food cost came in at 32.8%, slightly above the 31% target due to a batch of overprepped chicken that was wasted.`,
        keyMetric: { label: 'DT Wait', value: '4.9 min', trend: '+0.4 min' },
        driver: 'Payment window bottleneck',
      },
      {
        location: 'Joondalup',
        state: 'WA',
        sentiment: 'positive',
        narrative: `Joondalup posted $9,400 revenue (+5% vs avg). Customer ratings hit 4.7/5 this week, highest in 3 months. The team's focus on order accuracy (99.2%) is paying off. Labour cost at 25.1% is right on target.`,
        keyMetric: { label: 'Rating', value: '4.7/5', trend: '+0.3' },
        driver: 'Order accuracy focus',
      },
    ],
    topAction: 'Cross-train 2 additional crew at Prospect on grill station to prevent repeat of yesterday\'s bottleneck. Estimated time: 2 shifts (8 hrs). This would have saved ~$1,200 in lost revenue yesterday.',
    networkInsight: 'The suggestive sell script rollout at Southport is the biggest win this week. If replicated at Footscray and Prospect (who currently have the lowest combo attachment rates at 38% and 35%), projected network-wide upsell revenue could increase by $12,400/month.',
  };
};

const generateGenericNarratives = (industry) => {
  const now = new Date();
  const dayNames = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];
  const yesterday = dayNames[(now.getDay() + 6) % 7];
  const t = getIndustryTerms(industry);

  const narratives = {
    technology: {
      headline: `${yesterday} saw 47 new trial sign-ups (+18% vs ${yesterday} avg). MRR grew $2,400 from 3 plan upgrades. Churn risk flagged for 2 enterprise accounts.`,
      stories: [
        { title: 'Trial Conversion Surge', sentiment: 'positive', text: `Trial-to-paid conversion hit 24% this week, up from 19% average. The new onboarding email sequence (launched Monday) is the likely driver — click-through rates are 3x the previous sequence.`, metric: { label: 'Conversion', value: '24%', trend: '+5%' } },
        { title: 'Enterprise Churn Risk', sentiment: 'negative', text: `Two enterprise accounts (combined $8,400 MRR) haven't logged in for 14+ days. Both had support tickets filed last month about API rate limits. Recommend proactive outreach from CS team within 48 hours.`, metric: { label: 'At-Risk MRR', value: '$8,400', trend: 'alert' } },
      ],
      topAction: 'Schedule CS calls with the 2 at-risk enterprise accounts before Friday. Resolve API rate limit concerns — potential fix is a plan upgrade to Enterprise Plus.',
    },
    hospitality: {
      headline: `${yesterday} was above average — $18,200 in covers across lunch and dinner. Table turnover hit 3.2x, the best this month. Kitchen sent 94% of orders within 12 minutes.`,
      stories: [
        { title: 'Dinner Service Strong', sentiment: 'positive', text: `Dinner covers hit 78 (target: 65). The new seasonal tasting menu drove 34% of dinner revenue. Average spend per cover reached $62, up $8 from last ${yesterday}. Wine attachment rate was 71%.`, metric: { label: 'Avg Cover', value: '$62', trend: '+$8' } },
        { title: 'Lunch Labour Gap', sentiment: 'neutral', text: `Lunch was understaffed — 2 servers for 45 covers. Wait time averaged 8 minutes (target: 5 min). Two Google reviews mentioned slow service. Consider adding a 3rd server for ${yesterday} lunches.`, metric: { label: 'Wait', value: '8 min', trend: '+3 min' } },
      ],
      topAction: 'Add a 3rd server to the lunch roster on high-traffic days. The $180 labour cost is easily offset by the $400+ in additional covers you\'re losing to walkouts.',
    },
    general: {
      headline: `${yesterday} tracked ahead of plan — ${t.revenue.toLowerCase()} of $54,200, up 7% vs the ${yesterday} average, with steady margins and strong new-enquiry conversion.`,
      stories: [
        { title: `New ${t.customer} Conversion Up`, sentiment: 'positive', text: `Conversion of new enquiries reached 28% this week, up from 22%. Faster first response (under an hour) is the likely driver — ${t.customersLower} contacted quickly are far more likely to proceed.`, metric: { label: 'Conversion', value: '28%', trend: '+6%' } },
        { title: 'Overdue Invoices Building', sentiment: 'negative', text: `$14,200 across 3 accounts is now 30+ days overdue; two were promised payment last week. Recommend automated reminders plus a direct follow-up call this week to protect cash flow.`, metric: { label: 'Overdue', value: '$14,200', trend: 'alert' } },
      ],
      topAction: `Send reminders to the 3 overdue accounts today and call the two largest. Recovering this protects ~$14,200 in cash this month. Then introduce a one-hour response SLA for new ${t.customersLower} to sustain the conversion lift.`,
    },
  };

  return narratives[resolveIndustryKey(industry)] || narratives.general;
};

export const AINarrativeEngine = ({ industry = 'qsr', currentLocation }) => {
  const [expandedStory, setExpandedStory] = useState(null);
  const isQSR = industry === 'qsr';
  const data = isQSR ? generateNarratives() : null;
  const genericData = !isQSR ? generateGenericNarratives(industry) : null;

  const sentimentColors = {
    positive: 'border-l-emerald-500 bg-emerald-50/30',
    negative: 'border-l-red-400 bg-red-50/30',
    neutral: 'border-l-slate-300 bg-slate-50/30',
  };
  const sentimentIcons = {
    positive: <TrendingUp className="w-4 h-4 text-emerald-500" />,
    negative: <TrendingDown className="w-4 h-4 text-red-500" />,
    neutral: <ArrowRight className="w-4 h-4 text-slate-400" />,
  };

  return (
    <Card className="glass-card overflow-hidden" data-testid="ai-narrative-engine">
      <div className="h-1 bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500" />
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base sm:text-lg flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-indigo-500" />
            Daily Intelligence Briefing
          </CardTitle>
          <Badge variant="outline" className="text-[10px]">
            <Clock className="w-3 h-3 mr-1" />
            Updated {new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Headline */}
        <div className="bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-100 rounded-xl p-4" data-testid="narrative-headline">
          <div className="flex items-start gap-2">
            <Sparkles className="w-4 h-4 text-indigo-500 mt-0.5 shrink-0" />
            <p className="text-sm text-indigo-900 leading-relaxed font-medium">
              {isQSR ? data.headline : genericData.headline}
            </p>
          </div>
        </div>

        {/* Location Stories */}
        <div className="space-y-2">
          {(isQSR ? data.locationStories : genericData.stories).map((story, idx) => {
            const isExpanded = expandedStory === idx;
            const sentiment = story.sentiment || 'neutral';
            const metric = story.keyMetric || story.metric;
            return (
              <div
                key={idx}
                className={`border-l-4 ${sentimentColors[sentiment]} rounded-r-xl p-3 cursor-pointer transition-all hover:shadow-sm`}
                onClick={() => setExpandedStory(isExpanded ? null : idx)}
                data-testid={`narrative-story-${idx}`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {sentimentIcons[sentiment]}
                    <span className="text-sm font-semibold">
                      {story.location ? `${story.location}, ${story.state}` : story.title}
                    </span>
                    {metric && (
                      <Badge variant="outline" className="text-[10px] ml-1">
                        {metric.label}: {metric.value}
                      </Badge>
                    )}
                  </div>
                  <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                </div>
                {isExpanded && (
                  <div className="mt-2 space-y-2">
                    <p className="text-xs text-slate-700 leading-relaxed">
                      {story.narrative || story.text}
                    </p>
                    {story.driver && (
                      <div className="flex items-center gap-1.5 text-[10px] text-slate-500">
                        <span className="font-semibold">Key Driver:</span> {story.driver}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Top Action */}
        <div className="bg-amber-50 border border-amber-200/50 rounded-xl p-3" data-testid="narrative-top-action">
          <p className="text-[10px] uppercase tracking-wider text-amber-600 font-semibold mb-1">Priority Action</p>
          <p className="text-xs text-amber-800 leading-relaxed">
            {isQSR ? data.topAction : genericData.topAction}
          </p>
        </div>

        {/* Network Insight */}
        {isQSR && data.networkInsight && (
          <div className="bg-indigo-50/50 border border-indigo-100 rounded-xl p-3" data-testid="narrative-network-insight">
            <p className="text-[10px] uppercase tracking-wider text-indigo-500 font-semibold mb-1">Network Insight</p>
            <p className="text-xs text-indigo-700 leading-relaxed">{data.networkInsight}</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AINarrativeEngine;
